import { useMemo, useState } from 'react';
import type { SessionInfo } from '@voicer/shared';
import { useStore } from '../state/store';
import { controller } from '../conversation/controller';
import { ws } from '../ws/client';

function formatWhen(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function ConversationBrowser({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sessions = useStore((s) => s.sessions);
  const currentId = useStore((s) => s.session?.session.id);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sessions;
    return sessions.filter(
      (s) => s.name.toLowerCase().includes(needle) || s.preview.toLowerCase().includes(needle),
    );
  }, [sessions, q]);

  if (!open) return null;

  const load = async (s: SessionInfo) => {
    if (s.id === currentId) {
      onClose();
      return;
    }
    setBusy(s.id);
    try {
      await controller.switchConversation(s.id);
      onClose();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal convo-browser" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Past conversations">
        <div className="modal-head">
          <h3>Case conversations</h3>
          <span className="spacer" />
          <button className="small" onClick={onClose}>
            Close
          </button>
        </div>
        <input
          className="convo-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or preview…"
          autoFocus
        />
        <div className="convo-list">
          {!filtered.length && <div className="dim empty-inline">No conversations match.</div>}
          {filtered.map((s) => (
            <div key={s.id} className={`convo-item ${s.id === currentId ? 'active' : ''}`}>
              <button className="convo-main" disabled={busy === s.id} onClick={() => void load(s)}>
                <div className="convo-title">
                  <span className="convo-name">{s.name}</span>
                  {s.id === currentId && <span className="badge">current</span>}
                </div>
                <div className="convo-preview dim">{s.preview || 'Empty conversation'}</div>
                <div className="convo-meta dim">
                  {formatWhen(s.updatedAt)} · {s.messageCount} message{s.messageCount === 1 ? '' : 's'}
                </div>
              </button>
              <div className="convo-actions">
                <button
                  className="small"
                  title="Rename"
                  onClick={() => {
                    const name = prompt('Conversation name', s.name);
                    if (name?.trim()) void ws.rpc('session.rename', { id: s.id, name: name.trim() });
                  }}
                >
                  Rename
                </button>
                <button
                  className="small danger"
                  title="Delete"
                  onClick={() => {
                    if (!confirm(`Delete “${s.name}” and all its messages?`)) return;
                    void ws.rpc('session.delete', { id: s.id });
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
